import constants from "~~/constants";

type API_Response<T> = {
  data:
    | (T & {
        total?: number;
        totalPage?: number;
      })
    | null;
  message: string;
  success: boolean;
  errors: { msg: string }[];
  error: string | null;
  status: number;
  file: {
    url: string;
  };
};

export default function (baseUrl?: string) {
  const { baseURL } = useRuntimeConfig().public;
  const { locale, t } = useNuxtApp().$i18n;
  const userStore = useUserStore();
  const router = useRouter();
  const toast = useToast();

  let headers: Record<string, string> = {
    "accept-language": locale.value,
  };

  return {
    get: async function <T>(
      url: string,
      options?: {
        requiresAuthorization?: boolean;
        authToken?: string;
      },
    ): Promise<API_Response<T>> {
      try {
        if (options?.requiresAuthorization) {
          headers["Authorization"] =
            `Bearer ${options.authToken ?? userStore.authToken}`;
        }
        const response = await $fetch<API_Response<T>>(url, {
          method: "GET",
          baseURL: baseUrl ?? baseURL,
          headers,
          credentials: "include",
        });
        return response;
      } catch (error: any) {
        if (
          error.statusCode == constants.STATUS_UNAUTHORIZED &&
          options?.requiresAuthorization
        ) {
          const tokensRefreshed = await userStore.refreshTokens();
          if (tokensRefreshed) {
            headers["Authorization"] =
              `Bearer ${userStore.authToken}`;
            try {
              const response = await $fetch<API_Response<T>>(url, {
                method: "GET",
                baseURL: baseUrl ?? baseURL,
                headers,
                credentials: "include",
              });
              return response;
            } catch (retryError: any) {
              return retryError.data
                ? {
                    ...retryError.data,
                    error: retryError.data.message,
                    status: retryError.statusCode,
                  }
                : retryError;
            }
          } else {
            toast.add({
              title: t("toast.title.session-expired"),
              description: t("toast.description.sessions-expired"),
              color: "error",
            });
            userStore.logOut();
            router.push("/signin");
          }
        }
        return error.data
          ? {
              ...error.data,
              error: error.data.message,
              status: error.statusCode,
            }
          : error;
      }
    },
    post: async function <T>(
      url: string,
      body: any,
      options?: {
        requiresAuthorization?: boolean;
        authToken?: string;
      },
    ): Promise<API_Response<T>> {
      if (options?.requiresAuthorization) {
        headers["Authorization"] =
          `Bearer ${options.authToken ?? userStore.authToken}`;
      }
      try {
        const response = await $fetch<API_Response<T>>(url, {
          method: "POST",
          baseURL: baseUrl ?? baseURL,
          body,
          headers,
          credentials: "include",
        });
        return response;
      } catch (error: any) {
        if (
          error.statusCode == constants.STATUS_UNAUTHORIZED &&
          options?.requiresAuthorization
        ) {
          const tokensRefreshed = await userStore.refreshTokens();
          if (tokensRefreshed) {
            headers["Authorization"] =
              `Bearer ${userStore.authToken}`;
            try {
              const response = await $fetch<API_Response<T>>(url, {
                method: "POST",
                body,
                baseURL: baseUrl ?? baseURL,
                headers,
                credentials: "include",
              });
              return response;
            } catch (retryError: any) {
              return retryError.data
                ? {
                    ...retryError.data,
                    error: retryError.data.message,
                    status: retryError.statusCode,
                  }
                : retryError;
            }
          } else {
            toast.add({
              title: t("toast.title.session-expired"),
              description: t("toast.description.sessions-expired"),
              color: "error",
            });
            userStore.logOut();
            router.push("/signin");
          }
        }
        return error.data
          ? {
              ...error.data,
              error: error.data.message,
              status: error.statusCode,
            }
          : error;
      }
    },
    put: async function <T>(
      url: string,
      body: any,
      options?: {
        requiresAuthorization?: boolean;
        authToken?: string;
      },
    ): Promise<API_Response<T>> {
      if (options?.requiresAuthorization) {
        headers["Authorization"] =
          `Bearer ${options.authToken ?? userStore.authToken}`;
      }
      try {
        const response = await $fetch<API_Response<T>>(url, {
          method: "PUT",
          baseURL: baseUrl ?? baseURL,
          body,
          headers,
          credentials: "include",
        });
        return response;
      } catch (error: any) {
        if (
          error.statusCode == constants.STATUS_UNAUTHORIZED &&
          options?.requiresAuthorization
        ) {
          const tokensRefreshed = await userStore.refreshTokens();
          if (tokensRefreshed) {
            headers["Authorization"] =
              `Bearer ${userStore.authToken}`;
            try {
              const response = await $fetch<API_Response<T>>(url, {
                method: "PUT",
                body,
                baseURL: baseUrl ?? baseURL,
                headers,
                credentials: "include",
              });
              return response;
            } catch (retryError: any) {
              return retryError.data
                ? {
                    ...retryError.data,
                    error: retryError.data.message,
                    status: retryError.statusCode,
                  }
                : retryError;
            }
          } else {
            toast.add({
              title: t("toast.title.session-expired"),
              description: t("toast.description.sessions-expired"),
              color: "error",
            });
            userStore.logOut();
            router.push("/signin");
          }
        }
        return error.data
          ? {
              ...error.data,
              error: error.data.message,
              status: error.statusCode,
            }
          : error;
      }
    },
    delete: async function <T>(
      url: string,
      options?: {
        requiresAuthorization?: boolean;
        authToken?: string;
      },
    ): Promise<API_Response<T>> {
      if (options?.requiresAuthorization) {
        headers["Authorization"] =
          `Bearer ${options.authToken ?? userStore.authToken}`;
      }
      try {
        const response = await $fetch<API_Response<T>>(url, {
          method: "DELETE",
          baseURL: baseUrl ?? baseURL,
          headers,
          credentials: "include",
        });
        return response;
      } catch (error: any) {
        if (
          error.statusCode == constants.STATUS_UNAUTHORIZED &&
          options?.requiresAuthorization
        ) {
          const tokensRefreshed = await userStore.refreshTokens();
          if (tokensRefreshed) {
            try {
              const response = await $fetch<API_Response<T>>(url, {
                method: "DELETE",
                baseURL: baseUrl ?? baseURL,
                headers,
                credentials: "include",
              });
              return response;
            } catch (retryError: any) {
              return retryError.data
                ? {
                    ...retryError.data,
                    error: retryError.data.message,
                    status: retryError.statusCode,
                  }
                : retryError;
            }
          } else {
            toast.add({
              title: t("toast.title.session-expired"),
              description: t("toast.description.sessions-expired"),
              color: "error",
            });
            userStore.logOut();
            router.push("/signin");
          }
        }
        return error.data
          ? {
              ...error.data,
              error: error.data.message,
              status: error.statusCode,
            }
          : error;
      }
    },
    biPart: async function <T>(
      url: string,
      body: FormData,
      options?: {
        requiresAuthorization?: boolean;
        authToken?: string;
      },
    ): Promise<API_Response<T>> {
      if (options?.requiresAuthorization) {
        headers["Authorization"] =
          `Bearer ${options.authToken ?? userStore.authToken}`;
      }
      try {
        const response = await $fetch<API_Response<T>>(url, {
          method: "POST",
          baseURL: baseUrl ?? baseURL,
          body,
          headers,
          credentials: "include",
        });
        return response;
      } catch (error: any) {
        if (
          error.statusCode == constants.STATUS_UNAUTHORIZED &&
          options?.requiresAuthorization
        ) {
          const tokensRefreshed = await userStore.refreshTokens();
          if (tokensRefreshed) {
            try {
              const response = await $fetch<API_Response<T>>(url, {
                method: "POST",
                baseURL: baseUrl ?? baseURL,
                body,
                headers,
                credentials: "include",
              });
              return response;
            } catch (retryError: any) {
              return retryError.data
                ? {
                    ...retryError.data,
                    error: retryError.data.message,
                    status: retryError.statusCode,
                  }
                : retryError;
            }
          } else {
            toast.add({
              title: t("toast.title.session-expired"),
              description: t("toast.description.sessions-expired"),
              color: "error",
            });
            userStore.logOut();
            router.push("/signin");
          }
        }
        return error.data
          ? {
              ...error.data,
              error: error.data.message,
              status: error.statusCode,
            }
          : error;
      }
    },
  };
}
